/// <reference types="@angular/localize" />

import { bootstrapApplication } from "@angular/platform-browser";
import { NavigationEnd, provideRouter, Router, withHashLocation, withRouterConfig } from "@angular/router";
import { provideHttpClient, withXhr } from "@angular/common/http";
import { ErrorHandler, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from "@angular/core";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { providePrimeNG } from "primeng/config";
import { provideAngularSvgIcon } from "angular-svg-icon";
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module, ReCaptchaV3Service } from "ng-recaptcha-2";
import { MarkdownModule, MARKED_OPTIONS } from "ngx-markdown";
import { registerLocaleData } from "@angular/common";
import localeEn from "@angular/common/locales/en";
import localeIt from "@angular/common/locales/it";
import localeDe from "@angular/common/locales/de";
import localeFr from "@angular/common/locales/fr";
import localeEs from "@angular/common/locales/es";

import { AppComponent } from "./app/app.component";
import { routes } from "./app/app.routes";
import { TokenStorageService } from "./app/services/token-storage.service";
import { ApplicationDataService } from "./app/services/data/application-data.service";
import { TokenManagerService } from "./app/services/token-manager.service";
import { UserDataService } from "./app/services/data/user-data.service";
import { LoggerService } from "./ancestors/logger.service";
import { EnvironmentLoaderService } from "./ancestors/env-config.service";
import { ApeFooterService } from "./app/components/ape-footer/ape-footer.service";
import { TranslateService } from "./app/util/translate.service";
import { PrimengConfigService } from "./app/services/primeng-config.service";
import { GlobalError } from "./ancestors/exception.service";
import { i18nModule } from "./ancestors/i18n/i18n.module";
import { environment } from "./environments/environment";
import { MARKED_OPTIONS_CONFIG, PRIMENG_CONFIG } from "./app/constants/ui-providers.config";

// Registra i dati di localizzazione
registerLocaleData(localeEn, "en");
registerLocaleData(localeIt, "it");
registerLocaleData(localeDe, "de");
registerLocaleData(localeFr, "fr");
registerLocaleData(localeEs, "es");

/**
 * Inizializzatore dei servizi critici
 * Assicura che tutti i servizi necessari siano completamente inizializzati
 */
async function servicesInitializer(): Promise<void> {
  const envService = inject(EnvironmentLoaderService);
  const logger = inject(LoggerService);

  try {
    // Inizializza la configurazione dell'ambiente
    await envService.setAppConfig();
    logger.setCaller("ServicesInitializer");
    logger.info("Servizi critici inizializzati");
  } catch (error) {
    logger.setCaller("ServicesInitializer");
    logger.error("Errore inizializzazione servizi:");
    throw error;
  }
}

/**
 * Inizializzatore dell'app che preserva l'URL durante il refresh
 * Questo previene che i guards reindirizzino alla home quando l'utente fa F5
 */
function appInitializer(): void {
  // Preserva l'URL corrente se l'utente sta facendo refresh su una pagina protetta
  const currentUrl = window.location.hash;

  // Se siamo su una pagina protetta (non root), memorizziamo l'URL
  if (currentUrl && currentUrl !== "#/" && currentUrl !== "#") {
    sessionStorage.setItem("urlBeforeRefresh", currentUrl);

    // Imposta un flag temporaneo per prevenire il redirect iniziale
    sessionStorage.setItem("preventInitialRedirect", "true");
  }
}

/**
 * Inizializzatore del router ottimizzato per ripristino veloce dell'URL
 * Usa un approccio più diretto per ridurre i tempi di attesa
 */
function routerInitializer(): void {
  const router = inject(Router);
  const preservedUrl = sessionStorage.getItem("urlBeforeRefresh");
  const preventRedirect = sessionStorage.getItem("preventInitialRedirect");

  if (!preservedUrl || !preventRedirect) {
    return; // Niente da fare, esci subito
  }

  try {
    const targetPath = preservedUrl.startsWith("#/")
      ? preservedUrl.substring(2)
      : preservedUrl.substring(1);

    // Pulisci i flag PRIMA di qualsiasi operazione
    sessionStorage.removeItem("urlBeforeRefresh");
    sessionStorage.removeItem("preventInitialRedirect");

    if (targetPath && targetPath !== "") {
      // Naviga immediatamente dopo il primo NavigationEnd
      const subscription = router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          subscription.unsubscribe();
          // Naviga immediatamente - nessun delay
          router.navigateByUrl("/" + targetPath)
            .catch(() => {
              const logger = inject(LoggerService);
              logger.setCaller("RouterInitializer");
              logger.error("Errore durante il ripristino URL:");
            });
        }
      });

      // Fallback timeout breve
      setTimeout(() => {
        subscription.unsubscribe();
      }, 200);
    }
  } catch (error) {
    const logger = inject(LoggerService);
    logger.setCaller("RouterInitializer");
    logger.error("Errore durante il ripristino URL:");
  }
}

bootstrapApplication(AppComponent, {
  providers: [
    // Da Angular 21 il default è zoneless: questo provider è OBBLIGATORIO finché il layer HTTP
    // custom (fetch + Promise, vedi ancestors/base-api.service.ts) si affida a zone.js per la
    // change detection dei componenti OnPush. Senza, le viste restano sullo skeleton.
    provideZoneChangeDetection(),
    // Da Angular 22 il default di paramsInheritanceStrategy è 'always': pinniamo il comportamento
    // storico 'emptyOnly' per non cambiare la risoluzione di params/data lungo l'albero di routing.
    provideRouter(routes, withHashLocation(), withRouterConfig({ paramsInheritanceStrategy: "emptyOnly" })),
    provideHttpClient(withXhr()),
    provideAnimationsAsync(),
    providePrimeNG(PRIMENG_CONFIG),
    provideAngularSvgIcon(),
    importProvidersFrom(RecaptchaV3Module),
    importProvidersFrom(MarkdownModule.forRoot({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: MARKED_OPTIONS_CONFIG
      }
    })),
    TokenStorageService,
    ApplicationDataService,
    TokenManagerService,
    UserDataService,
    LoggerService,
    EnvironmentLoaderService,
    ApeFooterService,
    TranslateService,
    PrimengConfigService,
    {
      provide: ErrorHandler,
      useClass: GlobalError
    },
    ReCaptchaV3Service,
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptchaKey
    },
    i18nModule.setLocale(),
    i18nModule.setLocaleId(),
    // Services initializer - DEVE essere il primo per inizializzare i servizi critici
    provideAppInitializer(servicesInitializer),
    // App initializer per preservare l'URL durante il refresh
    provideAppInitializer(appInitializer),
    // Router initializer per ripristinare l'URL dopo l'inizializzazione
    provideAppInitializer(routerInitializer)
  ]
})
  .catch(err => {
    // eslint-disable-next-line no-console
    console.error(err);
  });
